import type { UseFormSetError } from 'react-hook-form';
import type { ApolloError } from '@apollo/client';

import { getLocalizationCountryCookie } from '@app/helpers/localization';
import { isNotNullish } from '@app/helpers/predicate';

import {
  ADDRESS_FIELD_KEYS,
  type AddressFormNamespaces,
} from '../Address.constants';
import type { AddressFormState, AddressType } from '../Address.types';
import { validationRules as phoneValidationRules } from '../AddressForm/PhoneField';
import { isAddressEqual, mapToAddressType } from '../helpers';
import type { CustomerAndCartAddressesQuery } from './__generated__/CustomerAndCartAddressesQuery';

export const getCustomerAddresses = (
  data?: CustomerAndCartAddressesQuery,
): AddressType[] | undefined =>
  data?.magento2?.customer?.addresses
    ?.map(mapToAddressType)
    .filter(isNotNullish);

export const getCartAddress = (
  data?: CustomerAndCartAddressesQuery,
): AddressType | null => {
  const cartAddress = mapToAddressType(
    data?.magento2?.customerCart?.billingAddress ?? null,
  );

  if (!cartAddress) return null;

  const customerAddressFromCart = getCustomerAddresses(data)?.find((address) =>
    isAddressEqual(address, cartAddress),
  );
  const relevantCartAddress = customerAddressFromCart || cartAddress;

  return isEligibleForStore(relevantCartAddress) ? relevantCartAddress : null;
};

export const getRelevantAddresses = (
  customerAddresses?: AddressType[],
  cartAddress?: AddressType | null,
): AddressType[] | null => {
  const addresses = customerAddresses?.length ? [...customerAddresses] : [];

  if (cartAddress && cartAddress?.__typename !== 'Magento2_CustomerAddress') {
    addresses.push(cartAddress);
  }

  return addresses.length ? addresses.filter(isEligibleForStore) : null;
};

/*
 * This assumes Magento 2 and our geolocation service
 * consistently use the same country spec (ISO-3166),
 * i.e. the cookie value is necessarily a valid address country value.
 */
export const isEligibleForStore = (address: AddressType | null): boolean =>
  address?.country_code === getLocalizationCountryCookie();

/*
 * This mitigates the missing required fields error (specifically Phone)
 * from old Magento 1 migrated addresses
 */
export const hasValidPhone = ({ telephone }: AddressType): boolean => {
  const { required, pattern } = phoneValidationRules;
  if (required && !telephone) {
    return false;
  }
  if (pattern && !telephone?.match(pattern)) {
    return false;
  }
  return true;
};

type ValidationError = {
  message: string;
  fields: string[];
  code: string;
};

export const setBackendValidationError = (
  namespace: AddressFormNamespaces,
  error: ApolloError,
  setError: UseFormSetError<AddressFormState>,
): void => {
  try {
    const objectMatches = error.message.match(/{([^}]+)}/g);

    if (!objectMatches) return;

    const errors: ValidationError[] = objectMatches?.map((match) =>
      JSON.parse(match),
    );

    if (errors) {
      const errorMessages: string[] = [];

      errors.forEach((error) => {
        error.fields.forEach((errorField) => {
          // Ignore identical messages
          if (errorMessages.includes(error.message)) {
            error.message = '';
          }

          ADDRESS_FIELD_KEYS.forEach((field) => {
            if (errorField === field) {
              setError(`${namespace}.${field}` as keyof AddressFormState, {
                message: error.message,
              });
            }
          });

          errorMessages.push(error.message);
        });
      });
    }
  } catch {
    // ignored since GraphQL errors are always thrown
  }
};
